/* eslint-disable react-hooks/exhaustive-deps */
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { useResizeContext } from "datastake-daf/dist/context";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Style from './style';
import SCALayout from './layouts/sca';
import DDSLayout from './layouts/dds';
import SCLLayout from './layouts/scl';
import AMPLayout from './layouts/amp';
import Header from './components/header';
import Sidebar from './components/sidebar';
import Loading from './components/loading';
import useQuery from '../../helpers/useQuery';
import NotFound from '../../components/NotFound';
import { PROJECTS } from '../../helpers/CanAccess';
import LoadingScreen from '../../components/Loading';
import { supportedModules } from '../../helpers/user';
import { supportedNamespaces, sourceChangedActions, clearActions, allActions } from './config';
import ProgramsLayout from './layouts/programs';

export default function Anaylis({ mode = 'app' }) {
  const { t } = useTranslation();
  const { isCollapsed: sidebarCollapsed } = useResizeContext();
  const { namespace, id, sourceId: selectedSourceId } = useParams();
  const { pathname } = useLocation();
  const { selectedProject: project, sourceId: pmeSelectedSource, user } = useSelector((state) => state.authentication);
  const analysis = useSelector((state) => state.analysis);
  const dispatch = useDispatch();
  const params = useQuery();
  const [activeSources, setActiveSources] = useState();
  const allowSourceChange = useMemo(() => namespace !== 'kyc', [namespace]);
  const [visibleSettings, setVisibleSettings] = useState(false);

  const language = useMemo(() => user?.language, [user]);

  const selectedProject = project;

  const [, mod] = mode === 'proxy' ?
    pathname.split(/\/proxy\/(pme|cadd|tif)\/analysis/)
    : pathname.split(/\/app\/(pme|cadd|tif)\/analysis/);

  const {
    loading,
    sources: backendSources,
    widgets,
    error,
    options,
    retrieved,
  } = useMemo(() => {
    if (analysis[namespace]) {
      return {
        ...analysis[namespace],
      };
    }
    return {
      sources: [],
      widgets: {},
      loading: false,
      error: true,
      options: {},
      retrieved: false,
    }
  }, [namespace, analysis]);

  const isSupported = useMemo(() => {
    if (mod) {
      if (supportedModules.includes(mod)) {
        if (supportedNamespaces[mod].includes(namespace)) {
          if ((namespace === 'dd' && selectedProject !== PROJECTS.myData) || error) {
            return false;
          }
          return true;
        }
      }
    }
    return false;
  }, [mod, error]);

  const sources = useMemo(() => {
    if (backendSources) {
      const featuredData = backendSources?.featuredData;
      if (featuredData) {
        if (featuredData.length === 2) {
          if (featuredData[0].id === featuredData[1].id) {
            return {
              ...backendSources,
              featuredData: [
                { ...featuredData[0] },
                { isTheSame: true },
              ],
            }
          }
        }
      }
    }

    return backendSources;
  }, [backendSources]);

  const allActiveSources = useMemo(() => {
    if (params.get('defaultSources')) {
      return [params.get('defaultSources')]
    }
    if (sources) {
      const all = [];
      if (sources.featuredData) {
        all.push(...sources.featuredData.filter((s) => !all.find(a => a === s.id)).map((s) => s.id))
      }
      if (sources.supplyChainActor) {
        all.push(...sources.supplyChainActor.filter((s) => !all.find(a => a === s.id)).map((s) => s.id))
      }
      if (sources.otherInformationSources) {
        all.push(...sources.otherInformationSources.filter((s) => !all.find(a => a === s.id)).map((s) => s.id))
      }
      return all;
    }
    return [];
  }, [sources]);

  const scaActiveSources = useMemo(() =>
    activeSources || allActiveSources, [activeSources, allActiveSources]);

  const isOwnData = useMemo(() => {
    return selectedProject === PROJECTS.myData ? true : false;
  }, [selectedProject]);

  const getRedirectLink = useCallback((sourceId, subjectId, customUrl, customNamespace) => {
    if (customUrl) {
      return `/${mode}/${mod}${customUrl}${sourceId ? `?sourceId=${sourceId}` : ''}`;
    }

    return `/${mode}/${mod}/analysis/${customNamespace || namespace}/${subjectId || id}${sourceId ? `/${sourceId}` : ''}`;
  }, [namespace, mod, id]);

  useEffect(() => () => {
    const action = clearActions[namespace];
    if (action) {
      dispatch(action());
    }
  }, []);

  const onChange = (sources) => {
    if (sourceChangedActions[namespace]) {
      const defaultSources = params.get('defaultSources');
      setActiveSources(sources);
      dispatch(sourceChangedActions[namespace]({ selectedSourceId, selectedProject, pmeSelectedSource, mod, defaultSources, id, sources, language }));
    }
  };

  const subjectName = useMemo(() => {
    if (analysis[namespace] && analysis[namespace].widgets && analysis[namespace].widgets.subjectDetails) {
      return analysis[namespace].widgets.subjectDetails.subjectName;
    }
    return t('Loading...');
  }, [namespace, analysis, t]);

  const datastakeId = useMemo(() => {
    if (analysis[namespace] && analysis[namespace].widgets && analysis[namespace].widgets.datastakeId) {
      return analysis[namespace].widgets.datastakeId;
    }
    return '';
  }, [namespace, analysis]);

  const hasDDSystems = useMemo(() => {
    if (analysis[namespace] && analysis[namespace].widgets && analysis[namespace].widgets.supplyChainDetails) {
      return analysis[namespace].widgets.supplyChainDetails.dueDiligenceSystems;
    }
    return false;
  }, [namespace, analysis]);

  const other = useMemo(() => {
    let defaultSources = params.get('defaultSources') || [];
    if (typeof defaultSources === 'string') {
      defaultSources = [defaultSources];
    }
    if (mod === 'pme') {
      return { selectedSourceId, selectedProject, pmeSelectedSource, mod, defaultSources, id, language }
    }
    if (mod === 'cadd' || mod === 'tif') {
      return { selectedSourceId, selectedProject, pmeSelectedSource: null, mod, defaultSources, id, language };
    }
    return {};
  }, [selectedSourceId, selectedProject, pmeSelectedSource, mod, id, params, language]);

  useEffect(() => {
    if (typeof allActions[namespace] === 'function' && mod === 'pme' && selectedProject === PROJECTS.myData) {
      dispatch(allActions[namespace](other));
    }
  }, [namespace, selectedSourceId, language]);

  useEffect(() => {
    if (typeof allActions[namespace] === 'function' && mod === 'pme' && selectedProject !== PROJECTS.myData && pmeSelectedSource) {
      dispatch(allActions[namespace](other));
    }
  }, [namespace, selectedSourceId, pmeSelectedSource, id, language]);

  useEffect(() => {
    if ((mod === 'cadd' || mod === 'tif') && typeof allActions[namespace] === 'function') {
      dispatch(allActions[namespace](other));
    }
  }, [namespace, selectedSourceId, id, language]);

  const headerSourceId = useMemo(() => {
    if (mod === 'pme' && (selectedProject && selectedProject !== PROJECTS.myData)
      && selectedSourceId) {
      return selectedSourceId;
    }
    if ((mod === 'cadd' || mod === 'tif') && selectedSourceId) {
      return selectedSourceId;
    }
    return null;
  }, [mod, selectedProject, pmeSelectedSource, selectedSourceId]);

  const allSubjectSources = useMemo(() => {
    if (sources) {
      const all = [];
      Object.values(sources).forEach((v) => {
        if (Array.isArray(v)) {
          v.forEach((val) => {
            if (!all.find((a) => a.id === val.id)) {
              all.push(val);
            }
          })
        }
      });
      return all;
    }
    return [];
  }, [sources]);

  const redirectToView = useCallback(({ link, nmspc = null, viewId = null, custom = '' }) => {
    if (custom) {
      return `/${mode}/${mod}${custom}`;
    }

    return `/${mode}/${mod}/view/${nmspc || namespace}/${viewId || id}${link ? `/${link}` : ''}`;
  }, [mod, namespace, id]);

  const isSingleSource = useMemo(() => {
    if (namespace === 'scp' || namespace === 'scl' || namespace === 'mine-operators' || namespace === 'amp') {
      if (mod === 'pme') {
        if (selectedSourceId || isOwnData || pmeSelectedSource !== 'overview') {
          return true;
        }
        return false;
      }
      if ((mod === 'cadd' || mod === 'tif')) {
        if (selectedSourceId || isOwnData) {
          return true;
        }
        return false;
      }
    }
    return true;
  }, [mod, selectedSourceId, isOwnData, pmeSelectedSource, namespace]);

  const getOption = useCallback((key, value) => {
    if (options) {
      if (options[key] && Array.isArray(options[key])) {
        const item = options[key].find((k) => k.value === value);
        if (item) {
          if (item.label[language]) {
            return item.label[language];
          }
        }
      } else if (typeof options[key] === 'object') {
        const item = options[key][value];
        if (item.label) {
          if (item.label[language]) {
            return item.label[language];
          }
        }
      }
    }
    return value;
  }, [options, language]);

  const layouts = {
    scp: <SCALayout
      sources={sources}
      data={widgets}
      namespace="scp"
      activeSources={scaActiveSources}
      isOwnData={isOwnData}
      isSingleSource={isSingleSource}
      mode={mode}
      getRedirectLink={getRedirectLink}
      other={other}
      redirectToView={redirectToView}
      selectedSource={selectedSourceId}
      getOption={getOption}
      hasDDSystems={hasDDSystems}
      sidebarCollapsed={sidebarCollapsed}
      loading={loading}
      setVisibleSettings={setVisibleSettings}
    />,
    'mine-operators': <SCALayout
      sources={sources}
      data={widgets}
      namespace="mine-operators"
      activeSources={scaActiveSources}
      isOwnData={isOwnData}
      isSingleSource={isSingleSource}
      mode={mode}
      getRedirectLink={getRedirectLink}
      other={other}
      redirectToView={redirectToView}
      selectedSource={selectedSourceId}
      getOption={getOption}
      hasDDSystems={hasDDSystems}
      sidebarCollapsed={sidebarCollapsed}
      loading={loading}
      setVisibleSettings={setVisibleSettings}
    />,
    kyc: <SCALayout
      activeSources={scaActiveSources}
      sources={sources}
      data={widgets}
      isOwnData={isOwnData}
      namespace="kyc"
      isSingleSource={isSingleSource}
      getRedirectLink={getRedirectLink}
      redirectToView={redirectToView}
      selectedSource={selectedSourceId}
      getOption={getOption}
      setVisibleSettings={setVisibleSettings}
      sidebarCollapsed={sidebarCollapsed}
      loading={loading}
    />,
    dd: <DDSLayout
      data={widgets}
      other={other}
      getOption={getOption}
      redirectToView={redirectToView}
      setVisibleSettings={setVisibleSettings}
    />,
    programs: <ProgramsLayout
      id={id}
      data={widgets}
      other={other}
      getOption={getOption}
      redirectToView={redirectToView}
      setVisibleSettings={setVisibleSettings}
    />,
    amp: <AMPLayout
      id={id}
      data={widgets}
      other={other}
      getOption={getOption}
      redirectToView={redirectToView}
      setVisibleSettings={setVisibleSettings}
    />,
    scl: <SCLLayout
      loading={loading}
      sources={sources}
      mod={mod}
      data={widgets}
      namespace="scp"
      activeSources={scaActiveSources}
      language={language}
      locationId={id}
      mode={mode}
      isOwnData={isOwnData}
      isSingleSource={isSingleSource}
      getRedirectLink={getRedirectLink}
      other={other}
      redirectToView={redirectToView}
      selectedSource={selectedSourceId}
      getOption={getOption}
      hasDDSystems={hasDDSystems}
      sidebarCollapsed={sidebarCollapsed}
      options={options}
      setVisibleSettings={setVisibleSettings}
    />
  };

  useEffect(() => () => {
    if (typeof clearActions[namespace] === 'function') {
      dispatch(clearActions[namespace]());
    }
  }, []);

  if (!isSupported) {
    return <NotFound />;
  }

  return (retrieved) ? (
    <Style>
      {(loading && namespace !== 'mine-operators' && namespace !== 'amp' && namespace !== 'scp' && namespace !== 'kyc' && namespace !== 'scl') && <Loading />}
      <Header
        namespace={namespace}
        id={id}
        isOwnData={isOwnData}
        mode={mode}
        datastakeId={datastakeId}
        sourceId={headerSourceId}
        selectedSourceId={selectedSourceId}
        mod={mod}
        subjectName={subjectName}
      />
      <div className="content">
        <Sidebar
          onChange={onChange}
          allActiveSources={allActiveSources}
          isOwnData={isOwnData}
          allSubjectSources={allSubjectSources}
          isLoading={loading}
          allowSourceChange={allowSourceChange}
          mode={mode}
          visibleSettings={visibleSettings}
          setVisibleSettings={setVisibleSettings}
        />
        <div className="view-content">
          {layouts[namespace]}
        </div>
      </div>
    </Style>
  ) : <LoadingScreen />;
}

